import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Eye, PackageCheck, Search, Truck } from "lucide-react";

const Page = styled.main`
  color: #172033;
`;

const Header = styled.section`
  margin-bottom: 18px;
  padding: 24px 26px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);

  h1 {
    margin: 0 0 8px;
    font-size: 30px;
  }

  p {
    margin: 0;
    color: #66758f;
  }
`;

const Toolbar = styled.section`
  display: grid;
  grid-template-columns: 1fr 160px 150px;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: #fff;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  color: #8da0b8;

  input {
    width: 100%;
    border: 0;
    outline: 0;
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
  color: #35445c;
  padding: 0 12px;
  font-weight: 800;
`;

const Table = styled.section`
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(32, 72, 122, 0.07);
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr 140px 130px 130px 112px;
  gap: 14px;
  align-items: center;
  padding: 15px 18px;
  border-bottom: 1px solid #edf2f8;

  &:last-child {
    border-bottom: 0;
  }

  &.head {
    background: #f7fbff;
    color: #66758f;
    font-size: 12px;
    font-weight: 900;
  }
`;

const OrderId = styled.strong`
  color: #2f80ed;
`;

const Customer = styled.div`
  strong {
    display: block;
  }

  span {
    color: #66758f;
    font-size: 12px;
  }
`;

const Status = styled.span`
  justify-self: start;
  border-radius: 999px;
  padding: 7px 10px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$fg};
  font-size: 12px;
  font-weight: 900;
`;

const Actions = styled.div`
  display: flex;
  gap: 7px;

  button {
    width: 34px;
    height: 34px;
    border: 1px solid #dce6f2;
    border-radius: 7px;
    background: #fff;
    color: #66758f;
    cursor: pointer;
  }
`;

const statusInfo = {
  paid: { label: "결제완료", bg: "#eaf3ff", fg: "#2f70cf" },
  preparing: { label: "상품준비", bg: "#fff7ed", fg: "#ea580c" },
  shipped: { label: "배송중", bg: "#ecfdf5", fg: "#059669" },
  completed: { label: "구매확정", bg: "#dcfce7", fg: "#16a34a" },
  cancelled: { label: "취소", bg: "#fee2e2", fg: "#dc2626" },
};

const orders = [
  { id: "AN-10091", customer: "김민준", email: "minjun@example.com", item: "GG 마틀라세 숄더백", amount: 2350000, status: "paid", date: "2026.05.03" },
  { id: "AN-10090", customer: "이서연", email: "seoyeon@example.com", item: "에어 조던 1 로우", amount: 129000, status: "shipped", date: "2026.05.02" },
  { id: "AN-10089", customer: "박지훈", email: "jihun@example.com", item: "로고 크루넥 티셔츠", amount: 890000, status: "preparing", date: "2026.05.01" },
  { id: "AN-10088", customer: "최유진", email: "yujin@example.com", item: "체크 백팩", amount: 1650000, status: "cancelled", date: "2026.04.30" },
];

export default function AdminOrders() {
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("all");

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const target = `${order.id} ${order.customer} ${order.item}`.toLowerCase();
      return (!keyword || target.includes(keyword.toLowerCase())) && (status === "all" || order.status === status);
    });
  }, [keyword, status]);

  return (
    <Page>
      <Header>
        <h1>주문 관리</h1>
        <p>결제, 출고, 배송, 취소 요청을 빠르게 처리합니다.</p>
      </Header>

      <Toolbar>
        <SearchBox><Search size={17} /><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="주문번호, 고객명, 상품명 검색" /></SearchBox>
        <Select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">전체 상태</option>
          {Object.entries(statusInfo).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
        </Select>
        <Select defaultValue="today"><option value="today">오늘</option><option value="week">최근 7일</option><option value="month">최근 30일</option></Select>
      </Toolbar>

      <Table>
        <Row className="head"><div>주문번호</div><div>고객</div><div>상품</div><div>금액</div><div>상태</div><div>작업</div></Row>
        {filteredOrders.map((order) => {
          const info = statusInfo[order.status];
          return (
            <Row key={order.id}>
              <OrderId>{order.id}</OrderId>
              <Customer><strong>{order.customer}</strong><span>{order.email}</span></Customer>
              <div>{order.item}</div>
              <b>₩ {order.amount.toLocaleString("ko-KR")}</b>
              <Status $bg={info.bg} $fg={info.fg}>{info.label}</Status>
              <Actions>
                <button type="button" title="상세보기"><Eye size={15} /></button>
                <button type="button" title="출고처리"><PackageCheck size={15} /></button>
                <button type="button" title="배송처리"><Truck size={15} /></button>
              </Actions>
            </Row>
          );
        })}
      </Table>
    </Page>
  );
}
